import {Button} from '@wandb/components';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import * as Dialog from '@wandb/weave/components/Dialog';
import {TextField} from '@wandb/weave/components/Form/TextField';
import React, {useState} from 'react';

import {useWorkspaceRefsContext} from '../../components/Workspace/WorkspaceRefsContext';
import {useUpdateRun} from '../../state/runs/hooks';
import {setCustomRunName as setCustomRunNameAction} from '../../state/views/customRunNames/actions';
import {useMaybeViewAction, usePartMaybe} from '../../state/views/hooks';
import {propagateErrorsContext} from '../../util/errors';
import {getCustomRunName} from '../../util/runs';

type RenameRunDialogProps = {
  runId: string;
  runName: string;
  runDisplayName: string;
  closeDialog: () => void;
};

export const RenameRunDialog = ({
  runId,
  runName,
  runDisplayName,
  closeDialog,
}: RenameRunDialogProps) => {
  const updateRun = useUpdateRun({
    context: propagateErrorsContext(),
  });
  const {customRunNamesRef} = useWorkspaceRefsContext();
  const customRunNames = usePartMaybe(customRunNamesRef);
  const customRunName = getCustomRunName(runName, false, customRunNames) ?? '';
  const setCustomRunName = useMaybeViewAction(
    customRunNamesRef,
    setCustomRunNameAction
  );

  const [newDisplayName, setNewDisplayName] = useState(runDisplayName);
  const [newCustomName, setNewCustomName] = useState(customRunName);

  const onSave = async () => {
    if (newDisplayName !== runDisplayName) {
      try {
        await updateRun(runId, runName, {displayName: newDisplayName});
      } catch (err) {
        toast('Failed to update run name', {type: 'error'});
      }
    }
    if (newCustomName !== customRunName) {
      setCustomRunName(runName, newCustomName);
    }
    closeDialog();
  };

  return (
    <Dialog.Root open onOpenChange={closeDialog}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content className="w-[500px] rounded-lg">
          <Dialog.Title>Edit run name</Dialog.Title>
          <Dialog.Description className="text-moon-700 dark:text-moon-300">
            Edit the run name for the entire project, or add a display name for
            this workspace only.
          </Dialog.Description>
          <div className="mt-16 mb-24 flex flex-col gap-12">
            <div className="flex flex-col gap-4">
              <label htmlFor="project-run-name" className="font-semibold">
                Run name
              </label>
              <TextField
                id="project-run-name"
                value={newDisplayName}
                onChange={setNewDisplayName}
                placeholder="Choose a run name"
                isContainerNightAware
              />
            </div>
            <div className="flex flex-col gap-4">
              <label htmlFor="workspace-run-name" className="font-semibold">
                Display name (this workspace)
              </label>
              <TextField
                id="workspace-run-name"
                value={newCustomName}
                onChange={setNewCustomName}
                placeholder={newDisplayName || 'Choose a display name'}
                isContainerNightAware
              />
            </div>
          </div>
          <div className="flex gap-8">
            <Button isDisabled={!newDisplayName} onPress={onSave}>
              Save
            </Button>
            <Button variant="secondary" onPress={closeDialog}>
              Cancel
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
